import React, {
  useEffect,
  useState,
} from "react";

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";

import { db } from "../firebase";

import "./WeeklyDepositBonusTZS.css";


/* =========================================================
   COLLECTIONS
========================================================= */

const TSH_COLLECTION =
  "tsh_transactions";

const VISITOR_COLLECTION =
  "visterdeposte";

const USERS_COLLECTION =
  "users";


/* =========================================================
   CURRENCY
========================================================= */

const TZS_CURRENCY =
  "TZS";

const TZS_BALANCE_FIELD =
  "tshBalance";


/* =========================================================
   WEEKLY BONUS SETTINGS
========================================================= */

const WEEKLY_PERCENT =
  5;


/* =========================================================
   COUNTRY → CURRENCY
========================================================= */

const countryCurrency = {

  Tanzania:
    "TZS",

  Kenya:
    "KES",

  Uganda:
    "UGX",

  Rwanda:
    "RWF",

  Burundi:
    "BIF",

  "DRC - Congo":
    "USD",

  Zambia:
    "ZMW",

  Malawi:
    "MWK",

  Mozambique:
    "MZN",

  Zimbabwe:
    "USD",

  "South Sudan":
    "SSP",

  Botswana:
    "BWP",

  Madagascar:
    "MGA",

};


/* =========================================================
   INTERNAL CURRENCY RATES
========================================================= */

const currencyRates = {

  Kenya:
    21.5,

  Uganda:
    0.77,

  Rwanda:
    1.9,

  Burundi:
    0.94,

  "DRC - Congo":
    2762.43,

  Zambia:
    147.23,

  Malawi:
    1.382,

  Mozambique:
    43.71,

  Zimbabwe:
    2762.43,

  "South Sudan":
    0.469,

  Botswana:
    211.95,

  Madagascar:
    0.684,

};


/* =========================================================
   FORMAT MONEY
========================================================= */

const formatMoney = (
  amount,
  currency = "TZS"
) => {

  return `${Number(
    amount || 0
  ).toLocaleString()} ${currency}`;

};


/* =========================================================
   NORMALIZE USERNAME
========================================================= */

const normalizeUsername = (
  username
) => {

  return String(
    username || ""
  )
    .trim()
    .toLowerCase();

};


/* =========================================================
   NORMALIZE STATUS
========================================================= */

const normalizeStatus = (
  status
) => {

  return String(
    status || ""
  )
    .trim()
    .toLowerCase();

};


/* =========================================================
   TIMESTAMP
========================================================= */

const getTimestampSeconds = (
  item
) => {

  const value =
    item?.createdAt ||
    item?.timestamp ||
    item?.paidAt ||
    item?.updatedAt;


  if (
    !value
  ) {

    return 0;

  }


  /* Firestore Timestamp */

  if (
    typeof value?.seconds ===
    "number"
  ) {

    return Number(
      value.seconds
    );

  }


  /* Firestore Timestamp with toDate() */

  if (
    typeof value?.toDate ===
    "function"
  ) {

    const date =
      value.toDate();


    if (
      date instanceof Date &&
      !Number.isNaN(
        date.getTime()
      )
    ) {

      return Math.floor(
        date.getTime() /
        1000
      );

    }

  }


  /* Numeric timestamp */

  if (
    typeof value ===
    "number"
  ) {

    return value >
      100000000000

      ? Math.floor(
          value / 1000
        )

      : Math.floor(
          value
        );

  }


  /* String date */

  const parsed =
    new Date(
      value
    ).getTime();


  if (
    Number.isNaN(
      parsed
    )
  ) {

    return 0;

  }


  return Math.floor(
    parsed /
    1000
  );

};


/* =========================================================
   WEDNESDAY CHECK
========================================================= */

const isWednesday = (
  timestampSeconds
) => {

  if (
    !timestampSeconds
  ) {

    return false;

  }


  const date =
    new Date(
      Number(
        timestampSeconds
      ) *
      1000
    );


  return (
    date.getDay() ===
    3
  );

};


/* =========================================================
   GET VISITOR CURRENCY
========================================================= */

const getVisitorCurrency = (
  item
) => {

  /* Stored currency */

  if (
    item?.currency
  ) {

    return String(
      item.currency
    )
      .trim()
      .toUpperCase();

  }


  /* Source currency */

  if (
    item?.sourceCurrency
  ) {

    return String(
      item.sourceCurrency
    )
      .trim()
      .toUpperCase();

  }


  /* Country fallback */

  return (

    countryCurrency[
      item?.country
    ] || ""

  );

};


/* =========================================================
   GET TRANSACTION CURRENCY
========================================================= */

const getTransactionCurrency = (
  item
) => {

  /* TSH collection = TZS */

  if (
    item?.source ===
    TSH_COLLECTION
  ) {

    return TZS_CURRENCY;

  }


  /* Visitor collection */

  return getVisitorCurrency(
    item
  );

};


/* =========================================================
   GET BALANCE FIELD
========================================================= */

const getBalanceFieldFromCurrency = (
  currency
) => {

  const normalized =
    String(
      currency || ""
    )
      .trim()
      .toUpperCase();


  /* TZS */

  if (
    normalized ===
    "TZS"
  ) {

    return TZS_BALANCE_FIELD;

  }


  /* Invalid */

  if (
    !normalized
  ) {

    return "";

  }


  /*
    Examples:

    KES → KESBalance
    UGX → UGXBalance
    USD → USDBalance
    RWF → RWFBalance
    BIF → BIFBalance
  */

  return `${normalized}Balance`;

};


/* =========================================================
   GET CURRENCY RATE
========================================================= */

const getCurrencyRate = (
  currency,
  country
) => {

  const normalizedCurrency =
    String(
      currency || ""
    )
      .trim()
      .toUpperCase();


  /* TZS */

  if (
    normalizedCurrency ===
    "TZS"
  ) {

    return 1;

  }


  /* Country first */

  if (
    country
  ) {

    const countryRate =
      Number(
        currencyRates[
          country
        ] || 0
      );


    if (
      countryRate > 0
    ) {

      return countryRate;

    }

  }


  /* Currency fallback */

  const matchingCountry =
    Object.keys(
      countryCurrency
    ).find(
      countryName =>

        String(
          countryCurrency[
            countryName
          ] || ""
        )
          .trim()
          .toUpperCase() ===
        normalizedCurrency
    );


  if (
    matchingCountry
  ) {

    return Number(
      currencyRates[
        matchingCountry
      ] || 0
    );

  }


  return 0;

};


/* =========================================================
   INTERNAL TZS EQUIVALENT
========================================================= */

const convertToTZSInternal = (
  amount,
  currency,
  country
) => {

  const numericAmount =
    Number(
      amount || 0
    );


  if (
    numericAmount <=
    0
  ) {

    return 0;

  }


  const normalizedCurrency =
    String(
      currency || ""
    )
      .trim()
      .toUpperCase();


  /* TZS */

  if (
    normalizedCurrency ===
    "TZS"
  ) {

    return Math.round(
      numericAmount
    );

  }


  const rate =
    getCurrencyRate(
      normalizedCurrency,
      country
    );


  if (
    rate <=
    0
  ) {

    return 0;

  }


  return Math.round(
    numericAmount *
    rate
  );

};


/* =========================================================
   GET WEDNESDAY KEY
========================================================= */

const getWednesdayKey = (
  timestampSeconds
) => {

  const date =
    new Date(
      Number(
        timestampSeconds
      ) *
      1000
    );


  const year =
    date.getFullYear();


  const month =
    String(
      date.getMonth() +
      1
    )
      .padStart(
        2,
        "0"
      );


  const day =
    String(
      date.getDate()
    )
      .padStart(
        2,
        "0"
      );


  return `${year}-${month}-${day}`;

};


/* =========================================================
   FIND USER
========================================================= */

const findUserByUsername = async (
  username
) => {

  if (
    !username
  ) {

    return null;

  }


  const usersRef =
    collection(
      db,
      USERS_COLLECTION
    );


  /* Exact search */

  try {

    const exactQuery =
      query(

        usersRef,

        where(
          "username",
          "==",
          username
        )

      );


    const snapshot =
      await getDocs(
        exactQuery
      );


    if (
      !snapshot.empty
    ) {

      return snapshot.docs[0];

    }

  }

  catch (
    error
  ) {

    console.warn(
      "Exact username lookup failed:",
      error
    );

  }


  /* Case insensitive fallback */

  const allUsers =
    await getDocs(
      usersRef
    );


  const normalized =
    normalizeUsername(
      username
    );


  return (

    allUsers.docs.find(
      userDoc =>

        normalizeUsername(
          userDoc.data()?.username
        ) ===
        normalized

    ) || null

  );

};


/* =========================================================
   NORMALIZE TSH DEPOSIT
========================================================= */

const normalizeTshDeposit = (
  document
) => {

  const data =
    document.data();


  const amount =
    Number(
      data.amount || 0
    );


  return {

    id:
      document.id,

    ...data,

    source:
      TSH_COLLECTION,

    sourceCurrency:
      TZS_CURRENCY,

    originalAmount:
      amount,

  };

};


/* =========================================================
   NORMALIZE VISITOR DEPOSIT
========================================================= */

const normalizeVisitorDeposit = (
  document
) => {

  const data =
    document.data();


  const amount =
    Number(
      data.amount || 0
    );


  const sourceCurrency =
    getVisitorCurrency(
      data
    );


  return {

    id:
      document.id,

    ...data,

    source:
      VISITOR_COLLECTION,

    sourceCurrency,

    originalAmount:
      amount,

  };

};


/* =========================================================
   GET WEDNESDAY SUCCESSFUL DEPOSITS
========================================================= */

const getWednesdaySuccessfulDeposits =
  async (
    username
  ) => {

    /* =====================================================
       TSH
    ===================================================== */

    const tshQuery =
      query(

        collection(
          db,
          TSH_COLLECTION
        ),

        where(
          "username",
          "==",
          username
        ),

        where(
          "status",
          "==",
          "success"
        )

      );


    /* =====================================================
       VISITOR
    ===================================================== */

    const visitorQuery =
      query(

        collection(
          db,
          VISITOR_COLLECTION
        ),

        where(
          "username",
          "==",
          username
        ),

        where(
          "status",
          "==",
          "success"
        )

      );


    /* =====================================================
       FETCH
    ===================================================== */

    const [
      tshSnapshot,
      visitorSnapshot,
    ] = await Promise.all([

      getDocs(
        tshQuery
      ),

      getDocs(
        visitorQuery
      ),

    ]);


    /* =====================================================
       NORMALIZE
    ===================================================== */

    const tshDeposits =
      tshSnapshot.docs.map(
        normalizeTshDeposit
      );


    const visitorDeposits =
      visitorSnapshot.docs.map(
        normalizeVisitorDeposit
      );


    /* =====================================================
       COMBINE
    ===================================================== */

    const allDeposits = [

      ...tshDeposits,

      ...visitorDeposits,

    ];


    /* =====================================================
       FILTER
    ===================================================== */

    const validDeposits =
      allDeposits.filter(
        item => {

          const timestamp =
            getTimestampSeconds(
              item
            );


          const depositType =
            String(
              item.depositOrderType ||
              item.position ||
              "other"
            )
              .trim()
              .toLowerCase();


          return (

            normalizeStatus(
              item.status
            ) ===
            "success" &&

            normalizeUsername(
              item.username
            ) ===
            normalizeUsername(
              username
            ) &&

            isWednesday(
              timestamp
            ) &&

            depositType ===
            "other" &&

            Number(
              item.originalAmount ||
              0
            ) > 0

          );

        }
      );


    /* =====================================================
       OLD → NEW
    ===================================================== */

    validDeposits.sort(
      (
        a,
        b
      ) => {

        const timeA =
          getTimestampSeconds(
            a
          );


        const timeB =
          getTimestampSeconds(
            b
          );


        if (
          timeA ===
          timeB
        ) {

          return String(
            a.id
          ).localeCompare(
            String(
              b.id
            )
          );

        }


        return (
          timeA -
          timeB
        );

      }
    );


    return validDeposits;

  };


/* =========================================================
   COMPONENT
========================================================= */

export default function WeeklyDepositBonusTZS() {

  const [
    username,
    setUsername,
  ] = useState("");


  const [
    deposit,
    setDeposit,
  ] = useState(null);


  const [
    bonus,
    setBonus,
  ] = useState(0);


  const [
    bonusCurrency,
    setBonusCurrency,
  ] = useState(
    TZS_CURRENCY
  );


  const [
    loading,
    setLoading,
  ] = useState(true);


  const [
    applying,
    setApplying,
  ] = useState(false);


  const [
    completed,
    setCompleted,
  ] = useState(false);


  const [
    message,
    setMessage,
  ] = useState("");


  /* =======================================================
     BACK
  ======================================================= */

  const handleBack = () => {

    if (
      window.history.length >
      1
    ) {

      window.history.back();

      return;

    }

    /*
      Fallback kama hakuna
      previous history.
    */

    window.location.href =
      "/";

  };


  /* =======================================================
     LOAD WEEKLY BONUS
  ======================================================= */

  useEffect(
    () => {

      const loadWeeklyBonus =
        async () => {

          try {

            setLoading(
              true
            );


            /* =================================================
               USERNAME
            ================================================= */

            const storedUsername =
              localStorage.getItem(
                "username"
              );


            if (
              !storedUsername
            ) {

              setMessage(
                "Login required."
              );

              return;

            }


            setUsername(
              storedUsername
            );


            /* =================================================
               FIND USER
            ================================================= */

            const userDoc =
              await findUserByUsername(
                storedUsername
              );


            if (
              !userDoc
            ) {

              setMessage(
                "Account not found."
              );

              return;

            }


            const userData =
              userDoc.data();


            /* =================================================
               GET WEDNESDAY DEPOSITS
            ================================================= */

            const deposits =
              await getWednesdaySuccessfulDeposits(
                storedUsername
              );


            if (
              deposits.length ===
              0
            ) {

              setMessage(
                "Hakuna deposit ya Jumatano inayostahili Weekly Bonus."
              );

              return;

            }


            /* =================================================
               CLAIMED TRANSACTION IDS
            ================================================= */

            const claimedIds =
              Array.isArray(
                userData.weeklyBonusClaimedTxIds
              )

                ? userData.weeklyBonusClaimedTxIds

                : [];


            /* =================================================
               FIND UNCLAIMED DEPOSIT
            ================================================= */

            const availableDeposit =
              deposits.find(
                tx => {

                  const uniqueKey =
                    `${tx.source}_${tx.id}`;


                  return (

                    !claimedIds.includes(
                      uniqueKey
                    ) &&

                    tx.selfBonusGiven !==
                    true

                  );

                }
              );


            /* =================================================
               NOTHING AVAILABLE
            ================================================= */

            if (
              !availableDeposit
            ) {

              setCompleted(
                true
              );

              setMessage(
                "Umeshapokea Weekly Deposit Bonus kwa deposit zako za Jumatano."
              );

              return;

            }


            /* =================================================
               ORIGINAL AMOUNT
            ================================================= */

            const originalAmount =
              Number(
                availableDeposit.originalAmount ||
                availableDeposit.amount ||
                0
              );


            if (
              originalAmount <=
              0
            ) {

              setMessage(
                "Deposit amount si sahihi."
              );

              return;

            }


            /* =================================================
               ORIGINAL CURRENCY
            ================================================= */

            const currency =
              getTransactionCurrency(
                availableDeposit
              );


            if (
              !currency
            ) {

              setMessage(
                "Deposit currency haijulikani."
              );

              return;

            }


            /* =================================================
               INTERNAL VALIDATION
            ================================================= */

            const internalTZSEquivalent =
              convertToTZSInternal(
                originalAmount,
                currency,
                availableDeposit.country
              );


            if (
              internalTZSEquivalent <=
              0
            ) {

              setMessage(
                "Unable to verify deposit currency."
              );

              return;

            }


            /* =================================================
               CALCULATE BONUS
            ================================================= */

            const calculatedBonus =
              Math.round(
                originalAmount *
                (
                  WEEKLY_PERCENT /
                  100
                )
              );


            if (
              calculatedBonus <=
              0
            ) {

              setMessage(
                "Bonus amount si sahihi."
              );

              return;

            }


            /* =================================================
               SET STATE
            ================================================= */

            setDeposit(
              availableDeposit
            );


            setBonus(
              calculatedBonus
            );


            setBonusCurrency(
              currency
            );


            setMessage(
              ""
            );

          }

          catch (
            error
          ) {

            console.error(
              "Weekly bonus loading error:",
              error
            );


            setMessage(
              "Unable to load Weekly Deposit Bonus."
            );

          }

          finally {

            setLoading(
              false
            );

          }

        };


      loadWeeklyBonus();

    },
    []
  );


  /* =======================================================
     APPLY BONUS
  ======================================================= */

  const applyBonus =
    async () => {

      if (
        !deposit ||
        !username ||
        !bonus ||
        applying ||
        completed
      ) {

        return;

      }


      setApplying(
        true
      );


      setMessage(
        ""
      );


      try {

        /* =================================================
           FIND USER
        ================================================= */

        const userDoc =
          await findUserByUsername(
            username
          );


        if (
          !userDoc
        ) {

          throw new Error(
            "User account not found."
          );

        }


        /* =================================================
           DEPOSIT REF
        ================================================= */

        const depositRef =
          doc(
            db,
            deposit.source,
            deposit.id
          );


        /* =================================================
           FINAL VALUES
        ================================================= */

        let finalBonus =
          0;


        let finalAmount =
          0;


        let finalCurrency =
          "";


        let finalBalanceField =
          "";


        let finalWeekKey =
          "";


        let finalTZSEquivalent =
          0;


        /* =================================================
           ATOMIC TRANSACTION
        ================================================= */

        await runTransaction(
          db,
          async (
            transaction
          ) => {

            /* =================================================
               READ DEPOSIT
            ================================================= */

            const depositSnapshot =
              await transaction.get(
                depositRef
              );


            if (
              !depositSnapshot.exists()
            ) {

              throw new Error(
                "Deposit haijapatikana."
              );

            }


            const freshDeposit =
              depositSnapshot.data();


            /* =================================================
               SUCCESS
            ================================================= */

            if (
              normalizeStatus(
                freshDeposit.status
              ) !==
              "success"
            ) {

              throw new Error(
                "Deposit si successful."
              );

            }


            /* =================================================
               DEPOSIT TIMESTAMP
            ================================================= */

            const depositTimestamp =
              getTimestampSeconds(
                freshDeposit
              );


            if (
              depositTimestamp <=
              0
            ) {

              throw new Error(
                "Deposit timestamp haijapatikana."
              );

            }


            /* =================================================
               WEDNESDAY
            ================================================= */

            if (
              !isWednesday(
                depositTimestamp
              )
            ) {

              throw new Error(
                "Deposit hii haikufanyika Jumatano."
              );

            }


            /* =================================================
               DEPOSIT ORDER TYPE
            ================================================= */

            const depositType =
              String(
                freshDeposit.depositOrderType ||
                freshDeposit.position ||
                "other"
              )
                .trim()
                .toLowerCase();


            if (
              depositType !==
              "other"
            ) {

              throw new Error(
                "Deposit hii si eligible kwa Weekly Bonus."
              );

            }


            /* =================================================
               READ USER
            ================================================= */

            const userSnapshot =
              await transaction.get(
                userDoc.ref
              );


            if (
              !userSnapshot.exists()
            ) {

              throw new Error(
                "User account not found."
              );

            }


            const userData =
              userSnapshot.data();


            /* =================================================
               CLAIMED IDS
            ================================================= */

            const claimedIds =
              Array.isArray(
                userData.weeklyBonusClaimedTxIds
              )

                ? userData.weeklyBonusClaimedTxIds

                : [];


            /* =================================================
               UNIQUE TRANSACTION KEY
            ================================================= */

            const uniqueTxKey =
              `${deposit.source}_${deposit.id}`;


            /* =================================================
               DOUBLE CLAIM PROTECTION
            ================================================= */

            if (
              claimedIds.includes(
                uniqueTxKey
              )
            ) {

              throw new Error(
                "Weekly Bonus hii tayari imetolewa."
              );

            }


            /* =================================================
               DEPOSIT SELF BONUS PROTECTION
            ================================================= */

            if (
              freshDeposit.selfBonusGiven ===
              true
            ) {

              throw new Error(
                "Bonus tayari imetolewa kwenye deposit hii."
              );

            }


            /* =================================================
               ADMIN BONUS PROTECTION
            ================================================= */

            if (
              freshDeposit.adminBonusGiven ===
              true
            ) {

              throw new Error(
                "Bonus tayari imelipwa na Admin."
              );

            }


            /* =================================================
               ORIGINAL AMOUNT
            ================================================= */

            finalAmount =
              Number(
                freshDeposit.amount ||
                0
              );


            if (
              finalAmount <=
              0
            ) {

              throw new Error(
                "Deposit amount haijapatikana."
              );

            }


            /* =================================================
               ORIGINAL CURRENCY
            ================================================= */

            finalCurrency =
              getTransactionCurrency({

                ...freshDeposit,

                source:
                  deposit.source,

              });


            if (
              !finalCurrency
            ) {

              throw new Error(
                "Deposit currency haijulikani."
              );

            }


            /* =================================================
               BALANCE FIELD
            ================================================= */

            finalBalanceField =
              getBalanceFieldFromCurrency(
                finalCurrency
              );


            if (
              !finalBalanceField
            ) {

              throw new Error(
                `Balance field haijapatikana kwa ${finalCurrency}.`
              );

            }


            /* =================================================
               INTERNAL TZS VALUE
            ================================================= */

            finalTZSEquivalent =
              convertToTZSInternal(
                finalAmount,
                finalCurrency,
                freshDeposit.country
              );


            if (
              finalTZSEquivalent <=
              0
            ) {

              throw new Error(
                "Unable to verify deposit currency."
              );

            }


            /* =================================================
               CALCULATE WEEKLY BONUS
            ================================================= */

            finalBonus =
              Math.round(
                finalAmount *
                (
                  WEEKLY_PERCENT /
                  100
                )
              );


            if (
              finalBonus <=
              0
            ) {

              throw new Error(
                "Weekly Bonus amount si sahihi."
              );

            }


            /* =================================================
               WEEK KEY
            ================================================= */

            finalWeekKey =
              getWednesdayKey(
                depositTimestamp
              );


            /* =================================================
               CURRENT BALANCE
            ================================================= */

            const currentBalance =
              Number(
                userData[
                  finalBalanceField
                ] || 0
              );


            const newBalance =
              currentBalance +
              finalBonus;


            /* =================================================
               UPDATE USER
            ================================================= */

            transaction.update(
              userDoc.ref,
              {

                [finalBalanceField]:
                  newBalance,

                weeklyBonusClaimedTxIds:
                  [
                    ...claimedIds,
                    uniqueTxKey,
                  ],

                lastWeeklyBonusAt:
                  serverTimestamp(),

                lastWeeklyBonusAmount:
                  finalBonus,

                lastWeeklyBonusCurrency:
                  finalCurrency,

                lastWeeklyBonusWeek:
                  finalWeekKey,

                lastWeeklyBonusTransactionId:
                  deposit.id,

                lastWeeklyBonusSource:
                  deposit.source,

              }
            );


            /* =================================================
               UPDATE DEPOSIT
            ================================================= */

            transaction.update(
              depositRef,
              {

                selfBonusGiven:
                  true,

                adminBonusGiven:
                  false,

                selfBonusAmount:
                  finalBonus,

                selfBonusClaimedAmount:
                  finalBonus,

                selfBonusPercent:
                  WEEKLY_PERCENT,

                selfBonusLabel:
                  "Weekly Deposit Bonus 5%",

                selfBonusType:
                  "weekly",

                selfBonusLocked:
                  false,

                selfBonusApprovedBy:
                  "user",

                selfBonusApprovedAt:
                  serverTimestamp(),

                originalDepositAmount:
                  finalAmount,

                sourceCurrency:
                  finalCurrency,

                bonusCurrency:
                  finalCurrency,

                bonusBalanceField:
                  finalBalanceField,

                bonusDepositTZSEquivalent:
                  finalTZSEquivalent,

                bonusDepositAmount:
                  finalAmount,

                bonusDepositCurrency:
                  finalCurrency,

                weeklyBonusWeek:
                  finalWeekKey,

                bonusStatus:
                  "credited",

                bonusTransactionType:
                  "depositBonus",

                depositOrderType:
                  "other",

                position:
                  "other",

              }
            );

          }
        );


        /* =================================================
           SUCCESS
        ================================================= */

        setCompleted(
          true
        );


        setBonus(
          finalBonus
        );


        setBonusCurrency(
          finalCurrency
        );


        setMessage(
          `Hongera! ${formatMoney(
            finalBonus,
            finalCurrency
          )} Weekly Bonus imeongezwa kwenye balance yako.`
        );

      }

      catch (
        error
      ) {

        console.error(
          "Weekly bonus payment failed:",
          error
        );


        setMessage(
          error?.message ||
          "Weekly Bonus payment failed."
        );

      }

      finally {

        setApplying(
          false
        );

      }

    };


  /* =======================================================
     LOADING
  ======================================================= */

  if (
    loading
  ) {

    return (

      <div className="wb-page">

        <div
          className="
            wb-card
            wb-loading
          "
        >

          <button
            type="button"
            className="wb-back-btn"
            onClick={handleBack}
            aria-label="Go back"
          >
            <span>
              ←
            </span>

            Back
          </button>


          <div
            className="
              wb-loader
            "
          />

          <p>
            Checking Weekly Deposit Bonus...
          </p>

        </div>

      </div>

    );

  }


  /* =======================================================
     CURRENT CURRENCY
  ======================================================= */

  const currentCurrency =
    deposit
      ? getTransactionCurrency(
          deposit
        )
      : bonusCurrency;


  /* =======================================================
     RENDER
  ======================================================= */

  return (

    <div className="wb-page">

      <div className="wb-card">


        {/* =================================================
            BACK BUTTON
        ================================================= */}

        <button
          type="button"
          className="wb-back-btn"
          onClick={handleBack}
          aria-label="Go back"
        >

          <span>
            ←
          </span>

          Back

        </button>


        {/* =================================================
            ICON
        ================================================= */}

        <div className="wb-gift">
          🎉
        </div>


        {/* =================================================
            BADGE
        ================================================= */}

        <div className="wb-badge">

          WEEKLY BONUS

        </div>


        {/* =================================================
            TITLE
        ================================================= */}

        <h2>

          Wednesday Deposit Bonus

        </h2>


        {/* =================================================
            SUBTITLE
        ================================================= */}

        <p
          className="
            wb-subtitle
          "
        >

          Hongera{" "}

          <strong>

            {username ||
              "Trader"}

          </strong>

          !

          <br />

          Deposit yako ya Jumatano
          inastahili Weekly Bonus.

          <br />

          Unaweza ku-claim bonus yako
          siku yoyote baada ya deposit
          ya Jumatano.

        </p>


        {/* =================================================
            DEPOSIT BOX
        ================================================= */}

        {deposit && (

          <div
            className="
              wb-deposit-box
            "
          >

            <div>

              <span>
                Deposit
              </span>

              <strong>

                {formatMoney(
                  deposit.originalAmount ||
                  deposit.amount ||
                  0,

                  currentCurrency
                )}

              </strong>

            </div>


            <div>

              <span>
                Bonus Rate
              </span>

              <strong>

                {WEEKLY_PERCENT}%

              </strong>

            </div>


            <div>

              <span>
                Currency
              </span>

              <strong>

                {currentCurrency}

              </strong>

            </div>


            <div>

              <span>
                Source
              </span>

              <strong>

                {
                  deposit.source ===
                  VISITOR_COLLECTION

                    ? "Visitor Deposit"

                    : "TZS Deposit"
                }

              </strong>

            </div>


            {deposit.country && (

              <div>

                <span>
                  Country
                </span>

                <strong>

                  {deposit.country}

                </strong>

              </div>

            )}

          </div>

        )}


        {/* =================================================
            BONUS BOX
        ================================================= */}

        {bonus > 0 && (

          <div
            className="
              wb-bonus-box
            "
          >

            <span>
              YOUR WEEKLY BONUS
            </span>


            <strong>

              {formatMoney(
                bonus,
                bonusCurrency
              )}

            </strong>


            <small>

              {WEEKLY_PERCENT}%
              Wednesday Deposit Bonus

            </small>

          </div>

        )}


        {/* =================================================
            MESSAGE
        ================================================= */}

        {message && (

          <div
            className={`
              wb-message

              ${
                completed
                  ? "wb-success"
                  : "wb-info"
              }
            `}
          >

            <span>

              {
                completed
                  ? "✓"
                  : "!"
              }

            </span>


            <p>

              {message}

            </p>

          </div>

        )}


        {/* =================================================
            APPLY
        ================================================= */}

        {bonus > 0 &&
        !completed && (

          <button
            type="button"
            className="
              wb-apply-btn
            "
            onClick={
              applyBonus
            }
            disabled={
              applying
            }
          >

            {applying ? (

              <>

                <span
                  className="
                    wb-button-loader
                  "
                />

                Processing...

              </>

            ) : (

              <>

                APPLY WEEKLY BONUS

                <span>
                  →
                </span>

              </>

            )}

          </button>

        )}


        {/* =================================================
            PAID
        ================================================= */}

        {completed && (

          <div
            className="
              wb-paid
            "
          >

            <span>
              ✓
            </span>

            Weekly Bonus Already Claimed

          </div>

        )}


        {/* =================================================
            NOTE
        ================================================= */}

        <p
          className="
            wb-note
          "
        >

          Weekly Deposit Bonus ni{" "}

          <strong>
            {WEEKLY_PERCENT}%
          </strong>

          {" "}ya deposit iliyofanyika Jumatano.

          <br />

          Unaweza ku-claim bonus yako
          siku yoyote baada ya Jumatano.

          <br />

          Bonus inaingia kwenye{" "}

          <strong>
            {bonusCurrency} balance
          </strong>

          yako.

        </p>


      </div>

    </div>

  );

}
